/**
 * Ansprechpartner-Bereich: Pill-Switch (Zentrale | Vertrieb) + Filter.
 *
 * - Switch (Muster wie bannerWithFilter.js): aktiver Tab + verschiebbares Pill,
 *   blendet den Vertrieb-Block (Karte + Filter) ein/aus und filtert die Karten
 *   nach data-category. Kategorie ist mehrwertig (Checkboxen Vertrieb/Zentrale)
 *   -> Karten koennen beiden Tabs angehoeren (Leerzeichen-getrennte Liste).
 * - Filter (Muster wie langSwitch.js): Dropdowns fuer Vertrieb (Gebiet,
 *   data-region - enthaelt Zonen UND den Zusatzwert "innendienst") und
 *   Zentrale (Abteilung, data-department). Gebiet/Abteilung sind Freitext
 *   (semikolongetrennt). Jedes Dropdown traegt data-filter-field="region"|
 *   "department" und ist nur im passenden Kategorie-Modus sichtbar (siehe
 *   _ansprechpartner.scss); "Alle anzeigen" zeigt alle Karten der aktiven
 *   Kategorie.
 *
 * - Karte (ansprechpartnerMap.js): Klick auf ein Vertriebsgebiet setzt den
 *   Gebietsfilter; Karte und Dropdown zeigen immer dieselbe Auswahl.
 *
 * Vanilla JS, kein jQuery.
 */
import { initAnsprechpartnerMap } from './ansprechpartnerMap.js';

export function initAnsprechpartner() {
    document.querySelectorAll('.js-ansprechpartner').forEach(root => {
        const switchEl = root.querySelector('.js-ansprechpartnerTabs');
        const tabs = Array.from(root.querySelectorAll('.js-ansprechpartnerTab'));
        const pill = root.querySelector('.ansprechpartner__indicator');
        const vertriebBlock = root.querySelector('.js-ansprechpartnerVertrieb');
        const cards = Array.from(root.querySelectorAll('.ansprechpartner__card'));
        const emptyMsg = root.querySelector('.js-ansprechpartnerEmpty');

        if (!tabs.length || !cards.length) return;

        let activeCategory = 'zentrale';
        const activeValues = { region: '', department: '' };

        // Semikolon-/Leerzeichen-getrennte data-Listen -> getrimmtes,
        // kleingeschriebenes String-Array. Gebiet/Abteilung sind Freitext,
        // daher case-insensitiv matchen (das PHP-Template dedupliziert die
        // Filter-Optionen ebenfalls case-insensitiv).
        const listOf = (value, sep) => (value || '').split(sep).map(s => s.trim().toLowerCase()).filter(Boolean);

        function applyFilter(animate = false) {
            let visible = 0;
            const entering = [];
            cards.forEach(card => {
                // Kategorie: Karte kann mehreren Kategorien angehoeren
                // (Mehrfach-Checkbox -> Leerzeichen-getrennte Liste).
                const matchCategory = listOf(card.dataset.category, ' ').includes(activeCategory);
                // Gebiet (nur Vertrieb): Freitext, semikolongetrennt; enthaelt
                // neben den Zonen auch den Zusatzwert "innendienst".
                const matchRegion =
                    activeCategory !== 'vertrieb' ||
                    activeValues.region === '' ||
                    listOf(card.dataset.region, ';').includes(activeValues.region);
                // Abteilung (nur Zentrale): Freitext, semikolongetrennt.
                const matchDepartment =
                    activeCategory !== 'zentrale' ||
                    activeValues.department === '' ||
                    listOf(card.dataset.department, ';').includes(activeValues.department);
                const match = matchCategory && matchRegion && matchDepartment;
                card.hidden = !match;
                if (match) {
                    visible++;
                    entering.push(card);
                }
            });

            // Sichtbare Karten gestaffelt einblenden (Animation in
            // _ansprechpartner.scss). Klasse erst entfernen, ein Reflow fuer
            // alle, dann neu setzen -> Animation startet bei jedem Filterwechsel.
            cards.forEach(card => card.classList.remove('is-entering'));
            if (animate && entering.length) {
                void root.offsetWidth;
                entering.forEach((card, i) => {
                    card.style.setProperty('--ap-i', String(Math.min(i, 8)));
                    card.classList.add('is-entering');
                });
            }

            // Hinweis ausgeben, wenn (z. B. fuer das gewaehlte Gebiet/die
            // Abteilung) keine Ansprechpartner vorhanden sind.
            if (emptyMsg) emptyMsg.hidden = visible !== 0;
        }

        function movePill(activeTab) {
            if (!pill || !activeTab) return;
            pill.style.left = activeTab.offsetLeft + 'px';
            pill.style.width = activeTab.offsetWidth + 'px';
        }

        function activate(index, animate = true) {
            const activeTab = tabs[index];
            activeCategory = activeTab.dataset.category;

            tabs.forEach((tab, i) => {
                const isActive = i === index;
                tab.classList.toggle('is-active', isActive);
                tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });

            const isVertrieb = activeCategory === 'vertrieb';
            if (vertriebBlock) {
                vertriebBlock.hidden = !isVertrieb;
            }
            root.classList.toggle('is-vertrieb', isVertrieb);
            root.classList.toggle('is-zentrale', activeCategory === 'zentrale');

            if (!animate && pill) {
                pill.style.transition = 'none';
            }
            movePill(activeTab);
            if (!animate && pill) {
                requestAnimationFrame(() => { pill.style.transition = ''; });
            }

            applyFilter(animate);
        }

        tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => activate(i));
        });

        // Filter-Dropdowns (Gebiet fuer Vertrieb, Abteilung fuer Zentrale).
        // Jedes traegt data-filter-field="region"|"department"; darueber wird
        // sowohl der zugehoerige activeValues-Eintrag als auch das dataset-Feld
        // an den Options-Buttons (data-region / data-department) ermittelt.
        const filters = Array.from(root.querySelectorAll('.js-ansprechpartnerFilter'));
        // Auswahl je Filterfeld - von Dropdown und Karte gemeinsam genutzt.
        const selectors = {};
        const map = initAnsprechpartnerMap(root, (value, text) => {
            if (!selectors.region) return;
            selectors.region(value, text);
            revealResults(value);
        });

        // Einspaltiges Layout (Karte ueber dem Grid): nach Gebietswahl zum
        // Ergebnis scrollen, sonst bleibt die Aenderung ausserhalb des Sichtfelds.
        function revealResults(value) {
            const regionFilter = root.querySelector('.js-ansprechpartnerFilter[data-filter-field="region"]');
            const mapEl = root.querySelector('.js-ansprechpartnerMap');
            if (!value || !regionFilter || !mapEl) return;
            if (regionFilter.getBoundingClientRect().top < mapEl.getBoundingClientRect().bottom) return;
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            regionFilter.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }

        filters.forEach(filter => {
            const field = filter.dataset.filterField;
            if (!field) return;

            const trigger = filter.querySelector('.js-ansprechpartnerFilterTrigger');
            const label = filter.querySelector('.js-ansprechpartnerFilterLabel');
            const options = Array.from(filter.querySelectorAll('.js-ansprechpartnerFilterOption'));

            const closeFilter = () => {
                filter.classList.remove('is-open');
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
            };

            if (trigger) {
                trigger.addEventListener('click', e => {
                    e.stopPropagation();
                    const open = filter.classList.toggle('is-open');
                    trigger.setAttribute('aria-expanded', String(open));
                });
            }

            // Kleingeschrieben ablegen -> passt zum lowercase-Array aus
            // listOf() beim Karten-Matching.
            const optionValue = option => (option.dataset[field] || '').trim().toLowerCase();

            // Wert setzen; Label kommt aus der passenden Option, sonst aus
            // fallbackText (Gebiet ohne hinterlegte Ansprechpartner).
            selectors[field] = (value, fallbackText) => {
                const match = options.find(o => optionValue(o) === value);
                activeValues[field] = value;
                options.forEach(o => o.classList.toggle('is-active', o === match));
                if (label) label.textContent = match ? match.textContent : fallbackText;
                if (field === 'region') map.sync(value);
                applyFilter(true);
            };

            options.forEach(option => {
                option.addEventListener('click', () => {
                    selectors[field](optionValue(option), option.textContent);
                    closeFilter();
                });
            });

            document.addEventListener('click', closeFilter);
            filter.addEventListener('keydown', e => {
                if (e.key === 'Escape') {
                    closeFilter();
                    if (trigger) trigger.focus();
                }
            });
        });

        // Initialzustand ohne Pill-Einflug setzen (aktiver Tab kommt aus dem
        // Markup -> Standard ist "Vertrieb").
        const initialIndex = Math.max(0, tabs.findIndex(t => t.classList.contains('is-active')));
        activate(initialIndex, false);

        // Pill nach Font-Load / Resize neu positionieren (Barlow laedt ggf. asynchron)
        const reposition = () => {
            const current = tabs.findIndex(t => t.classList.contains('is-active'));
            if (current >= 0) {
                if (pill) pill.style.transition = 'none';
                movePill(tabs[current]);
                if (pill) requestAnimationFrame(() => { pill.style.transition = ''; });
            }
        };

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(reposition);
        }
        if (typeof ResizeObserver !== 'undefined' && switchEl) {
            new ResizeObserver(reposition).observe(switchEl);
        }
    });
}
